
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { JsonSchema, ValidationError } from '@/content/schema';
import { ancestors, changedAt, errorsWithin, flatten, matches, nodeFor, nodePath, ownFieldsChanged, OutlineNode, ROOT_ID } from '@/content/outline';

/**
 * The parts of a content page as a tree: click one to edit it. The search narrows the tree to
 * the parts whose own text holds every word; Enter opens the first.
 */
@Component
export default class ContentOutline extends Vue {
  @Prop({ type: Array, required: true }) readonly nodes!: OutlineNode[];
  /** Id of the part being edited. */
  @Prop({ required: true }) readonly active!: string;
  @Prop({ required: true }) readonly schema!: JsonSchema;
  @Prop({ type: Array, default: () => [] }) readonly errors!: ValidationError[];
  @Prop({ default: null }) readonly value!: unknown;
  @Prop({ default: null }) readonly savedValue!: unknown;

  search = '';
  /** Parts the editor opened or closed by hand; the way to the active part is always open. */
  userOpen: string[] = [];

  get searching(): boolean {
    return Boolean(this.search && this.search.trim());
  }

  get matchList(): OutlineNode[] {
    return this.searching ? flatten(this.nodes).filter((node) => matches(node, this.search)) : [];
  }

  get matchCount(): number {
    return this.matchList.length;
  }

  /** While searching every part is open, so the hits show; otherwise the way to the active part plus what was opened by hand. */
  get openIds(): string[] {
    if (this.searching) return flatten(this.nodes).filter((node) => node.children && node.children.length).map((node) => node.id);
    return [...new Set([...this.userOpen, ...ancestors(this.nodes, this.active)])];
  }

  @Watch('active')
  onActiveChange(): void {
    this.userOpen = [...new Set([...this.userOpen, ...ancestors(this.nodes, this.active)])];
  }

  filter(item: OutlineNode, search: string): boolean {
    return matches(item, search);
  }

  onActive(ids: string[]): void {
    // A click on the active part would deselect it; it stays open instead.
    if (ids.length && ids[0] !== this.active) this.$emit('select', ids[0]);
  }

  onOpen(ids: string[]): void {
    if (!this.searching) this.userOpen = ids;
  }

  openFirstMatch(): void {
    const first = this.matchList[0];
    if (first) this.$emit('select', first.id, this.search.trim());
  }

  /** Errors of the part and everything under it; for "Algemeen" only those of the page's own fields. */
  errorCount(node: OutlineNode): number {
    if (node.id === ROOT_ID) return this.errors.filter((error) => nodeFor(this.nodes, error.path)?.id === ROOT_ID).length;
    return errorsWithin(this.errors, nodePath(node.id));
  }

  changed(node: OutlineNode): boolean {
    if (this.savedValue === null) return false;
    if (node.id === ROOT_ID) return ownFieldsChanged(this.schema, this.value, this.savedValue);
    return changedAt(this.value, this.savedValue, nodePath(node.id));
  }
}
